/**
 * Plays one library video in place: a poster with its running time, and the
 * hosted player (Wistia, YouTube or Vimeo) once the coach presses play. The
 * iframe loads only on that click, so a page with several videos does not pay
 * for players nobody starts.
 *
 * Aliases: library video player, course video, intro video, wistia player
 */

import { useState } from "react";

import { VideoEmbed, VideoPosterButton } from "~/components/ui/video-embed";
import { cn } from "~/lib/cn";
import type { PlayableVideo } from "~/lib/shared/learning/playable-video";
import {
  formatVideoDuration,
  videoPosterCandidates,
} from "~/lib/shared/video-url";

/** A video's running time over its poster's bottom-right corner. */
export function VideoDurationBadge({ seconds }: { seconds: number }) {
  return (
    <span className="pointer-events-none absolute right-3 bottom-3 rounded-md bg-gray-900/70 px-1.5 py-0.5 text-xs font-medium text-white tabular-nums">
      {formatVideoDuration(seconds)}
    </span>
  );
}

export function LibraryVideoPlayer({
  video,
  className,
}: {
  video: PlayableVideo;
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div
        className={cn(
          "aspect-video w-full overflow-hidden rounded-2xl bg-gray-900",
          className,
        )}
      >
        <VideoEmbed autoPlay title={video.title} url={video.url} />
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <VideoPosterButton
        aria-label={`Play video: ${video.title}`}
        candidates={videoPosterCandidates(video)}
        onClick={() => setPlaying(true)}
      />
      {video.durationSeconds !== null && (
        <VideoDurationBadge seconds={video.durationSeconds} />
      )}
    </div>
  );
}
